import React from 'react';
import { useEditorContext, TEXT_ELEMENT_IDS } from '../../context/EditorContext';
import type { TemplateLayer } from '../../types';

interface LayerToggleProps {
  layers: TemplateLayer[];
  /** Legacy overrides — kept for backward compat; EditorContext visibility takes precedence */
  overrides: Record<string, boolean>;
  onToggle: (layerId: string, visible: boolean) => void;
}

const LAYER_TYPE_ICONS: Record<string, string> = {
  background: '🖼',
  anchor: '🎥',
  logo: '⭐',
  'top-bar': '━',
  'lower-third': '📋',
  ticker: '📰',
  'breaking-news-banner': '🚨',
  watermark: '💧',
  audio: '🔊',
  image: '🖼',
  video: '📹',
};

export function LayerToggle({ layers, overrides, onToggle }: LayerToggleProps) {
  const { state, toggleLayerVisibility, toggleTextElementVisibility } = useEditorContext();
  const config = state.editableConfig;

  // Headline / Subheadline visibility (defaults to true if not set)
  const headlineVisible = config?.headline.visible ?? true;
  const subheadlineVisible = config?.subheadline.visible ?? true;

  // Sort layers by zIndex descending (top layers first in UI)
  const sorted = [...layers].sort((a, b) => b.position.zIndex - a.position.zIndex);

  return (
    <div className="space-y-3 p-4">
      <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Layers</h3>
      <div className="space-y-1">

        {/* ── Text elements: Headline + Subheadline ── */}
        <TextElementRow
          label="Headline"
          icon="T"
          iconColor="#fbbf24"
          visible={headlineVisible}
          onToggle={() => toggleTextElementVisibility(TEXT_ELEMENT_IDS.HEADLINE)}
        />
        <TextElementRow
          label="Subheadline"
          icon="T"
          iconColor="#a3e635"
          visible={subheadlineVisible}
          onToggle={() => toggleTextElementVisibility(TEXT_ELEMENT_IDS.SUBHEADLINE)}
        />

        {/* Divider */}
        {sorted.length > 0 && (
          <div className="border-t border-zinc-700/60 my-2" />
        )}

        {/* ── Regular layers ── */}
        {sorted.map((layer) => {
          // EditorContext is the source of truth for visibility
          const editorLayer = config?.layers.find((l) => l.id === layer.id);
          const visible = editorLayer?.visible ?? overrides[layer.id] ?? layer.visible;
          const icon = LAYER_TYPE_ICONS[layer.type] ?? '◻';

          return (
            <button
              key={layer.id}
              type="button"
              onClick={() => {
                // Toggle via EditorContext (keeps undo/redo history)
                toggleLayerVisibility(layer.id);
                // Also update legacy overrides for compositionProps fallback
                onToggle(layer.id, !visible);
              }}
              className={[
                'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors',
                visible
                  ? 'bg-zinc-800 hover:bg-zinc-700'
                  : 'bg-zinc-900 opacity-50 hover:opacity-70',
              ].join(' ')}
            >
              <VisibilityDot visible={visible} />
              <span className="text-sm shrink-0 select-none">{icon}</span>
              <span className={['flex-1 text-xs truncate', visible ? 'text-zinc-200' : 'text-zinc-500'].join(' ')}>
                {layer.label}
              </span>
              <span className="text-[9px] font-mono text-zinc-600 shrink-0">{layer.type}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────

function VisibilityDot({ visible }: { visible: boolean }) {
  return (
    <div className={['w-3 h-3 rounded-full shrink-0 transition-colors', visible ? 'bg-blue-500' : 'bg-zinc-600'].join(' ')} />
  );
}

function TextElementRow({
  label, icon, iconColor, visible, onToggle,
}: { label: string; icon: string; iconColor: string; visible: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={[
        'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-colors',
        visible ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-zinc-900 opacity-50 hover:opacity-70',
      ].join(' ')}
    >
      <VisibilityDot visible={visible} />
      <span
        style={{ color: iconColor, fontWeight: 800, fontSize: 12, minWidth: 18, textAlign: 'center' }}
        className="shrink-0 select-none"
      >
        {icon}
      </span>
      <span className={['flex-1 text-xs truncate', visible ? 'text-zinc-200' : 'text-zinc-500'].join(' ')}>
        {label}
      </span>
      <span className="text-[9px] font-mono text-zinc-600 shrink-0">text</span>
    </button>
  );
}
